import React, { useState, useEffect, useRef } from "react";
import iconMapping from "./iconMapping";
import { AnswerMode, StaticAnswerModes } from "./AnswerMode";
import CreateAnswerModeModal from "../CreateAnswerModeModal/CreateAnswerModeModal";
import { CustomAnswerModeDB } from "../../indexedDB/CustomAnswerModeDB";
import {
  Add20Regular,
  Edit20Regular,
  ChevronUp20Regular,
  ChevronDown20Regular,
  Checkmark20Regular,
  Edit16Regular,
  Delete20Regular,
} from "@fluentui/react-icons";

import styles from "./AnswerModeContainer.module.css";

interface Props {
  onAnswerModeChosen: (answerMode: AnswerMode) => void;
  numDocuments: number;
  selectedAnswerMode: AnswerMode;
  isExpanded: boolean;
  setIsExpanded: (expanded: boolean) => void;
  onModalStateChange?: (isOpen: boolean) => void;
  refreshKey?: number;
}

const customDB = new CustomAnswerModeDB();

const AnswerModeContainer = ({
  onAnswerModeChosen,
  numDocuments,
  selectedAnswerMode,
  isExpanded,
  setIsExpanded,
  onModalStateChange,
  refreshKey,
}: Props) => {
  const [combinedList, setCombinedList] = useState<AnswerMode[]>([]);
  const [activeButton, setActiveButton] = useState<string | null>(null);

  // UI States
  const [isModalOpen, setModalOpen] = useState(false);
  const [editingModeData, setEditingModeData] = useState<AnswerMode | null>(
    null,
  );
  const [isEditMode, setIsEditMode] = useState(false);

  // Drag States
  const [isDragging, setIsDragging] = useState(false);
  const [draggedItemIndex, setDraggedItemIndex] = useState<number | null>(null);

  // NEW: Ref to track if the mouse is specifically on the handle
  const dragHandleActiveRef = useRef(false);

  // REF for Auto-Scrolling
  const gridRef = useRef<HTMLDivElement>(null);

  // Notify parent of modal state changes
  useEffect(() => {
    onModalStateChange?.(isModalOpen);
  }, [isModalOpen, onModalStateChange]);

  // --- Auto Expand/Collapse based on Edit Mode ---
  useEffect(() => {
    if (isEditMode) {
      setIsExpanded(true);
    } else {
      setIsExpanded(false);
    }
  }, [isEditMode, setIsExpanded]);

  // --- 1. Fetch & Filter Logic (UNCHANGED) ---
  useEffect(() => {
    const fetchModes = async () => {
      try {
        let customModes: AnswerMode[] = [];
        let savedOrder: string[] | null = null;
        try {
          customModes = await customDB.getAllModes();
          savedOrder = await customDB.getGlobalOrder();
        } catch (e) {
          console.warn("DB init warning", e);
        }

        const normalizedCustom = customModes.map((m) => ({
          ...m,
          contextRequirement: m.contextRequirement || "any",
        }));

        const allAvailableModes = [...normalizedCustom, ...StaticAnswerModes];
        const hasDocs = numDocuments > 0;

        // Filter
        const filtered = allAvailableModes.filter((mode) => {
          if (mode.id === "standard") return false;
          if (mode.id === "compare") return numDocuments >= 2;
          if (mode.contextRequirement === "any") return true;
          if (mode.contextRequirement === "documents") return hasDocs;
          if (mode.contextRequirement === "no_documents") return !hasDocs;
          return true;
        });

        // Sort
        filtered.sort((a, b) => {
          const getWeight = (mode: AnswerMode) => {
            if (hasDocs && mode.contextRequirement === "documents") return 2;
            if (hasDocs && mode.contextRequirement === "any") return 1;
            return 0;
          };
          if (!isEditMode) {
            const weightA = getWeight(a);
            const weightB = getWeight(b);
            if (weightA !== weightB) return weightB - weightA;
          }

          if (savedOrder && savedOrder.length > 0) {
            const posA = savedOrder.indexOf(a.id);
            const posB = savedOrder.indexOf(b.id);
            const idxA = posA !== -1 ? posA : 999;
            const idxB = posB !== -1 ? posB : 999;
            if (idxA !== idxB) return idxA - idxB;
          }
          return (a.orderIndex ?? 0) - (b.orderIndex ?? 0);
        });

        setCombinedList(filtered);

        if (selectedAnswerMode.id === "standard") setActiveButton(null);
        else setActiveButton(selectedAnswerMode.name);
      } catch (error) {
        console.error("Error fetching modes:", error);
      }
    };
    fetchModes();
  }, [numDocuments, isEditMode, isModalOpen, selectedAnswerMode, refreshKey]);

  // --- 2. Handlers ---

  const handleButtonClick = (mode: AnswerMode) => {
    if (activeButton === mode.name) {
      const standard = StaticAnswerModes.find((m) => m.id === "standard");
      if (standard) onAnswerModeChosen(standard);
      setActiveButton(null);
    } else {
      onAnswerModeChosen(mode);
      setActiveButton(mode.name);
    }
  };

  const handleEditClick = (mode: AnswerMode, e: React.MouseEvent) => {
    e.stopPropagation();
    if (mode.isCustom) {
      setEditingModeData(mode);
      setModalOpen(true);
    }
  };

  const handleSaveMode = async (mode: AnswerMode) => {
    const isNew = !combinedList.some((m) => m.id === mode.id);
    await customDB.addOrUpdateMode(mode);

    if (isNew) {
      const currentOrder = combinedList.map((m) => m.id);
      const newOrder = [mode.id, ...currentOrder];
      await customDB.saveGlobalOrder(newOrder);
    }

    setModalOpen(false);
    setIsEditMode(false);
    onAnswerModeChosen(mode);
    setActiveButton(mode.name);

    if (isNew) {
      setIsExpanded(true);
      setTimeout(() => {
        if (gridRef.current) {
          gridRef.current.scrollTo({
            top: gridRef.current.scrollHeight,
            behavior: "smooth",
          });
        }
      }, 150);
    }
  };

  const handleDeleteMode = async (id: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation(); // Only stop propagation if an event is passed
    await customDB.deleteMode(id);

    const currentGlobalOrder = await customDB.getGlobalOrder();
    if (currentGlobalOrder) {
      const newOrder = currentGlobalOrder.filter((orderId) => orderId !== id);
      await customDB.saveGlobalOrder(newOrder);
    }

    setCombinedList((prev) => prev.filter((m) => m.id !== id));
    if (activeButton === combinedList.find((m) => m.id === id)?.name) {
      const standard = StaticAnswerModes.find((m) => m.id === "standard");
      if (standard) onAnswerModeChosen(standard);
      setActiveButton(null);
    }
  };

  // --- 3. Drag Logic (FIXED) ---
  const handleDragStart = (e: React.DragEvent, index: number) => {
    if (!isEditMode) return;

    // KEY FIX: Only allow dragging if the handle is currently being held
    // This uses the ref we set in onMouseDown/onMouseUp
    if (!dragHandleActiveRef.current) {
      e.preventDefault();
      return;
    }

    setIsDragging(true);
    e.dataTransfer.effectAllowed = "move";
    setDraggedItemIndex(index);

    // Optional: Improve drag ghost image if needed
    // const dragImg = document.createElement("div");
    // e.dataTransfer.setDragImage(dragImg, 0, 0);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (!isEditMode || draggedItemIndex === null || draggedItemIndex === index)
      return;

    const newList = [...combinedList];
    const itemToMove = newList.splice(draggedItemIndex, 1)[0];
    newList.splice(index, 0, itemToMove);
    setCombinedList(newList);
    setDraggedItemIndex(index);
  };

  const handleDragEnd = async () => {
    setIsDragging(false);
    setDraggedItemIndex(null);
    dragHandleActiveRef.current = false; // Reset handle state
    if (isEditMode) {
      const newOrderIds = combinedList.map((m) => m.id);
      await customDB.saveGlobalOrder(newOrderIds);
    }
  };

  return (
    <div className={styles.mainContainer}>
      <div
        className={`${styles.controlBar} ${isExpanded ? styles.expanded : ""}`}
      >
        <div className={styles.gridViewport} ref={gridRef}>
          <div className={styles.modeGrid}>
            {combinedList.map((mode, index) => {
              const IconComponent =
                iconMapping[mode.icon] || iconMapping["default"];
              const isActive = activeButton === mode.name;

              return (
                <div
                  key={mode.id}
                  className={`
                      ${styles.modeWrapper} 
                      ${isEditMode ? styles.shake : ""} 
                      ${isEditMode ? styles.editWrapper : ""} 
                      ${
                        draggedItemIndex === index
                          ? styles.hiddenPlaceholder
                          : ""
                      }
                    `}
                  draggable={isEditMode}
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                >
                  {isEditMode ? (
                    <div className={styles.editModeCard}>
                      {/* 1. DELETE BUTTON (Left) */}
                      {mode.isCustom ? (
                        <button
                          className={styles.deleteButton}
                          onClick={(e) => handleDeleteMode(mode.id, e)}
                          title="Delete"
                        >
                          <Delete20Regular />
                        </button>
                      ) : (
                        <div className={styles.deleteSpacer} />
                      )}

                      {/* 2. ICON & LABEL (Center) */}
                      <div className={styles.editLabelContainer}>
                        {IconComponent && (
                          <IconComponent className={styles.editIcon} />
                        )}
                        <span className={styles.label}>{mode.name}</span>
                      </div>

                      {/* 3. RIGHT ACTIONS */}
                      <div className={styles.rightActions}>
                        {mode.isCustom && (
                          <button
                            className={styles.editActionButton}
                            onClick={(e) => handleEditClick(mode, e)}
                            title="Edit"
                          >
                            <Edit16Regular />
                          </button>
                        )}

                        {/* THE 6-DOT GRIP HANDLE */}
                        <div
                          className={styles.dragHandleBox}
                          title="Drag to reorder"
                          // KEY FIX: Track mouse state on the handle
                          onMouseDown={() =>
                            (dragHandleActiveRef.current = true)
                          }
                          onMouseUp={() =>
                            (dragHandleActiveRef.current = false)
                          }
                          onMouseLeave={() =>
                            (dragHandleActiveRef.current = false)
                          }
                          onTouchStart={() =>
                            (dragHandleActiveRef.current = true)
                          }
                          onTouchEnd={() =>
                            (dragHandleActiveRef.current = false)
                          }
                        >
                          <div className={styles.dot} />
                          <div className={styles.dot} />
                          <div className={styles.dot} />
                          <div className={styles.dot} />
                          <div className={styles.dot} />
                          <div className={styles.dot} />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <button
                      className={`
                        ${styles.modeButton} 
                        ${isActive ? styles.active : ""}
                      `}
                      onClick={() => handleButtonClick(mode)}
                      title={mode.description}
                    >
                      {IconComponent && (
                        <IconComponent className={styles.icon} />
                      )}
                      <span className={styles.label}>{mode.name}</span>
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer / Utility Bar */}
        <div className={styles.utilityBar}>
          <div className={styles.leftTools}>
            <button
              className={`${styles.utilButton} ${
                isEditMode ? styles.utilActive : ""
              }`}
              onClick={() => {
                if (isEditMode) {
                  setIsEditMode(false);
                  setIsExpanded(false);
                } else {
                  setIsEditMode(true);
                  setIsExpanded(true);
                }
              }}
              title={isEditMode ? "Done Editing" : "Manage AI Assistants"}
            >
              {isEditMode ? <Checkmark20Regular /> : <Edit20Regular />}
            </button>

            <button
              className={styles.utilButton}
              onClick={() => {
                setEditingModeData(null);
                setModalOpen(true);
              }}
              disabled={isEditMode}
              title="Create New AI Assistant"
            >
              <Add20Regular />
            </button>
          </div>

          <button
            className={styles.expandToggle}
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? <ChevronDown20Regular /> : <ChevronUp20Regular />}
          </button>
        </div>
      </div>

      <CreateAnswerModeModal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSaveMode}
        onDelete={async (id?: string) => {
          // Fallback to editingModeData.id if the modal doesn't pass an ID back
          const targetId = id || editingModeData?.id;

          if (targetId) {
            await handleDeleteMode(targetId);
            setModalOpen(false);
            setEditingModeData(null);
          }
        }}
        nextOrderIndex={combinedList.length}
        existingModeNames={combinedList.map((m) => m.name)}
        initialMode={editingModeData}
      />
    </div>
  );
};

export default AnswerModeContainer;
